import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Kanban, Lock, ShieldAlert } from 'lucide-react';
import { Spinner } from '@/components/common';

const PIN_LENGTH = 6;

const fmtCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export default function Login() {
  const { login } = useApp();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState('login'); // login | setup
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [lockRemaining, setLockRemaining] = useState(0);

  useEffect(() => {
    axios.get(`${API_BASE}/auth/status`).then(({ data }) => {
      if (!data.pin_set) setMode('setup');
      if (data.locked && data.retry_after_seconds > 0) setLockRemaining(data.retry_after_seconds);
    }).catch(() => {});
  }, []);

  const locked = lockRemaining > 0;

  // Lockout countdown ticker
  useEffect(() => {
    if (!locked) return undefined;
    const t = setInterval(() => setLockRemaining((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [locked]);

  const submit = async () => {
    if (pin.length < PIN_LENGTH || locked) return;
    setBusy(true);
    setError('');
    try {
      const url = mode === 'setup' ? `${API_BASE}/auth/setup` : `${API_BASE}/auth/login`;
      const { data } = await axios.post(url, { pin });
      login(data.token);
      navigate('/');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429 && detail?.retry_after_seconds) {
        setLockRemaining(detail.retry_after_seconds);
        setError('');
      } else {
        setError(typeof detail === 'string' ? detail : detail?.message || 'Login failed');
      }
      setPin('');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm shadow-lg">
        <CardContent className="p-8">
          <form data-testid="pin-login-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <div className="flex flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                <Kanban className="h-6 w-6" />
              </div>
              <h1 data-testid="login-app-name" className="mt-4 text-xl font-bold tracking-tight">ChromiumHQ</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {mode === 'setup' ? 'Create your 6-digit PIN to secure your command center' : 'Enter your 6-digit PIN to access your command center'}
              </p>
              <div className="mt-6">
                <InputOTP data-testid="pin-login-otp-input" maxLength={PIN_LENGTH} value={pin} onChange={setPin} disabled={locked}>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-11 w-10 text-lg sm:h-12 sm:w-11" />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {locked && (
                <Alert variant="destructive" className="mt-4">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertDescription data-testid="pin-login-lockout">
                    Too many failed attempts. Locked for <span data-testid="pin-login-lockout-countdown" className="font-semibold tabular-nums">{fmtCountdown(lockRemaining)}</span>
                  </AlertDescription>
                </Alert>
              )}
              {!locked && error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription data-testid="pin-login-error">{error}</AlertDescription>
                </Alert>
              )}
              <Button data-testid="pin-login-submit-button" type="submit" disabled={pin.length < PIN_LENGTH || busy || locked} className="mt-6 w-full gap-2">
                {busy ? <Spinner /> : <Lock className="h-4 w-4" />}
                {locked ? `Locked · ${fmtCountdown(lockRemaining)}` : mode === 'setup' ? 'Set PIN & Enter' : 'Unlock'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
